// MpfForm.props.ts
import { MpfInputDateProps } from '../MpfInputDate/MpfInputDate.props';
import { MpfInputTextProps } from '../MpfInputText/MpfInputText.props';
import { MpfRadiosProps } from '../MpfRadios/MpfRadios.props';
import { MpfCheckboxesProps } from '../MpfCheckboxes/MpfCheckboxes.props';
import { HeadingLevel, HeadingLevelSize } from '../MpfInput/MpfInputBaseProps';
import { MpfInputTextareaProps } from '../MpfInputTextarea/MpfInputTextarea.props';
import { MpfAttachmentsProps } from '../MpfAttachments/MpfAttachments.props';
import type { MpfContentNode } from '../MpfContentRenderer/MpfContentRenderer.props';
import { MpfSelectProps } from '../MpfSelect/MpfSelect.props';
import { MpfInputBloodPressureProps } from '../MpfInputBloodPressure/MpfInputBloodPressure.props';
import type { MpfButtonVariant } from '../MpfButton/MpfButton.props';

// ENG-21793: 'number' is a text input with inputmode="numeric" — NHS DS has no number component
export const MpfInputTextTypes = ['text', 'email', 'phone', 'postcode', 'number'] as const;
export type MpfInputTextType = (typeof MpfInputTextTypes)[number];

export type MpfFormFieldType =
  | MpfInputTextType
  | 'attachments'
  | 'textarea'
  | 'radios'
  | 'checkboxes'
  | 'date'
  | 'dateOfBirth'
  | 'select'
  | 'bloodPressure';

export type FormFieldValue =
  | string // text, email, phone, postcode, textarea, date, radios
  | (string | number | null)[] // checkboxes (multi select)
  | null
  | File[];

// Input types that basically use plaintext but might have validation for its value
export function isMpfInputTextType(type: MpfFormFieldType): type is MpfInputTextType {
  return (MpfInputTextTypes as readonly string[]).includes(type);
}

//
export type MpfFormFieldConfig =
  | (MpfFormDateProps & { type: 'dateOfBirth' | 'date' })
  | (MpfRadiosProps & { type: 'radios' })
  | (MpfAttachmentsProps & { type: 'attachments' })
  | (MpfCheckboxesProps & { type: 'checkboxes' })
  | (MpfInputTextareaProps & { type: 'textarea' })
  | (MpfInputTextProps & {
      type: MpfInputTextType;
      initialValue?: FormFieldValue;
    })
  | MpfFormSelectProps
  | (MpfInputBloodPressureProps & {
      type: 'bloodPressure';
      initialValue?: FormFieldValue;
    });

export interface MpfFormDateProps extends Omit<MpfInputDateProps, 'modelValue'> {
  modelValue?: string;
  type: 'dateOfBirth' | 'date';
}

export interface MpfFormSelectProps extends Omit<MpfSelectProps, 'modelValue' | 'initialValue'> {
  modelValue?: string | number | null;
  initialValue?: FormFieldValue;
  type: 'select';
}

// Content blocks sit in the same ordered array as inputs — position is the
// template's structure — but carry no name, no value and no validation.
export const MpfFormContentTypes = ['description', 'infoBanner'] as const;
export type MpfFormContentType = (typeof MpfFormContentTypes)[number];

export interface MpfFormDescriptionConfig {
  type: 'description';
  content: MpfContentNode[];
}

export interface MpfFormInfoBannerConfig {
  type: 'infoBanner';
  heading: string;
  text?: string;
  content?: MpfContentNode[];
}

export type MpfCheckboxesFieldConfig = MpfCheckboxesProps & { type: 'checkboxes' };

export type MpfFormContentConfig = MpfFormDescriptionConfig | MpfFormInfoBannerConfig;

export type MpfFormBlockConfig = MpfFormFieldConfig | MpfFormContentConfig;

// Without this, content blocks pick up an empty value keyed `undefined`, sit in
// the error maps and reach the submit payload.
export function isInputField(block: MpfFormBlockConfig): block is MpfFormFieldConfig {
  return !(MpfFormContentTypes as readonly string[]).includes(block.type);
}
export type FormError = { name: string; errors: string[] | string };

export interface MpfFormProps {
  headingLevel?: HeadingLevel;
  headingLevelSize?: HeadingLevelSize;
  formTitle?: string;
  hint?: string;
  fields: MpfFormBlockConfig[];
  mode?: 'edit' | 'create' | 'view';
  formErrors?: FormError[];
  submitLabel?: string;
  /** ENG-21800 `warning` for destructive submits, e.g. cancelling an appointment. */
  submitVariant?: MpfButtonVariant;
  errorSummaryByLine?: string;
  errorSummaryTitle?: string;
  showErrors?: boolean;
}

export const mpfFormDefaults = {
  submitLabel: 'Submit',
  submitVariant: 'primary' as MpfButtonVariant,
  mode: 'edit',
  headingLevel: 2 as HeadingLevel,
  headingLevelSize: 'l' as HeadingLevelSize,
  errorSummaryByLine: 'Please fix the following errors:',
  errorSummaryTitle: 'There is a problem',
} satisfies Partial<MpfFormProps>;

export interface ResponseFormErrors {
  name: string;
  errors: string[] | string;
}
